import { useState, useEffect } from 'react';
import { useAuthStore } from '../../store/useStore';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { 
  Cake, 
  Mail, 
  MessageSquare, 
  Send, 
  Gift, 
  Calendar,
  Users,
  Settings2,
  History,
  Play
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function BirthdaySettings() {
  const { token } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    send_email: true,
    send_sms: true,
    send_whatsapp: true,
    send_week_before: true,
    send_day_before: true,
    send_same_day: true,
    include_coupon: true,
    coupon_type: 'percentage',
    coupon_value: 15,
    coupon_validity_days: 30,
    template: {
      subject: '🎂 ¡Feliz Cumpleaños de parte de {restaurant_name}!',
      email_body: '',
      sms_body: '',
      whatsapp_body: ''
    }
  });
  const [upcoming, setUpcoming] = useState({ same_day: 0, day_before: 0, week_before: 0, customers: {} });
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchSettings();
    fetchUpcoming();
    fetchHistory();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/birthday/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcoming = async () => {
    try {
      const res = await fetch(`${API_URL}/api/birthday/upcoming`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUpcoming(data);
      }
    } catch (err) {
      console.error('Error fetching upcoming:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API_URL}/api/birthday/history?limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.messages || []);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/birthday/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      
      if (res.ok) {
        toast.success('Configuración guardada');
      } else {
        toast.error('Error al guardar');
      }
    } catch (err) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };

  const processNow = async () => {
    try {
      const res = await fetch(`${API_URL}/api/birthday/process`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.result.total_sent} mensajes enviados`);
        fetchHistory();
      } else {
        toast.error('Error al procesar');
      }
    } catch (err) {
      toast.error('Error de conexión');
    }
  };

  if (loading) {
    return <div className="animate-pulse space-y-4">
      <div className="h-10 bg-muted rounded" />
      <div className="h-40 bg-muted rounded" />
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="birthday-settings">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-green-500/10 flex items-center justify-center">
                <Cake className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{upcoming.same_day}</p>
                <p className="text-xs text-muted-foreground">Cumpleaños Hoy</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-yellow-500/10 flex items-center justify-center">
                <Calendar className="h-5 w-5 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{upcoming.day_before}</p>
                <p className="text-xs text-muted-foreground">Mañana</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{upcoming.week_before}</p>
                <p className="text-xs text-muted-foreground">Esta Semana</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="settings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="settings">
            <Settings2 className="h-4 w-4 mr-2" /> Configuración
          </TabsTrigger>
          <TabsTrigger value="template">
            <Mail className="h-4 w-4 mr-2" /> Plantillas
          </TabsTrigger>
          <TabsTrigger value="history">
            <History className="h-4 w-4 mr-2" /> Historial
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Mensajes de Cumpleaños</CardTitle>
                  <CardDescription>Configura el envío automático de felicitaciones</CardDescription>
                </div>
                <Switch
                  checked={settings.enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
                  data-testid="birthday-enabled-switch"
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Channels */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Canales de envío</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center justify-between p-3 bg-muted rounded-xl">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-primary" />
                      <span className="text-sm">Email</span>
                    </div>
                    <Switch
                      checked={settings.send_email}
                      onCheckedChange={(checked) => setSettings({ ...settings, send_email: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded-xl">
                    <div className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-green-500" />
                      <span className="text-sm">SMS</span>
                    </div>
                    <Switch
                      checked={settings.send_sms}
                      onCheckedChange={(checked) => setSettings({ ...settings, send_sms: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded-xl">
                    <div className="flex items-center gap-2">
                      <Send className="h-4 w-4 text-green-600" />
                      <span className="text-sm">WhatsApp</span>
                    </div>
                    <Switch
                      checked={settings.send_whatsapp}
                      onCheckedChange={(checked) => setSettings({ ...settings, send_whatsapp: checked })}
                    />
                  </div>
                </div>
              </div>

              {/* Timing */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Momento de envío</Label>
                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center justify-between p-3 bg-muted rounded-xl">
                    <span className="text-sm">Una semana antes</span>
                    <Switch
                      checked={settings.send_week_before}
                      onCheckedChange={(checked) => setSettings({ ...settings, send_week_before: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded-xl">
                    <span className="text-sm">Un día antes</span>
                    <Switch
                      checked={settings.send_day_before}
                      onCheckedChange={(checked) => setSettings({ ...settings, send_day_before: checked })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded-xl">
                    <span className="text-sm">El mismo día</span>
                    <Switch
                      checked={settings.send_same_day}
                      onCheckedChange={(checked) => setSettings({ ...settings, send_same_day: checked })}
                    />
                  </div>
                </div>
              </div>

              {/* Coupon */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-sm font-medium">Incluir Cupón de Regalo</Label>
                  <Switch
                    checked={settings.include_coupon}
                    onCheckedChange={(checked) => setSettings({ ...settings, include_coupon: checked })}
                  />
                </div>
                {settings.include_coupon && (
                  <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-xl">
                    <div>
                      <Label className="text-xs">Tipo de descuento</Label>
                      <Select
                        value={settings.coupon_type}
                        onValueChange={(value) => setSettings({ ...settings, coupon_type: value })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                          <SelectItem value="fixed">Monto fijo ($)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Valor</Label>
                      <Input
                        type="number"
                        value={settings.coupon_value}
                        onChange={(e) => setSettings({ ...settings, coupon_value: parseFloat(e.target.value) })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Validez (días)</Label>
                      <Input
                        type="number"
                        value={settings.coupon_validity_days}
                        onChange={(e) => setSettings({ ...settings, coupon_validity_days: parseInt(e.target.value) })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={processNow}>
              <Play className="h-4 w-4 mr-2" /> Procesar Ahora
            </Button>
            <Button onClick={saveSettings} disabled={saving}>
              {saving ? 'Guardando...' : 'Guardar Configuración'}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="template" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Plantillas de Mensaje</CardTitle>
              <CardDescription>Personaliza los mensajes de cumpleaños</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-3 bg-muted rounded-xl text-xs">
                <p className="font-medium mb-1">Variables disponibles:</p>
                <code>{'{customer_name}'}</code>, <code>{'{restaurant_name}'}</code>, <code>{'{coupon_code}'}</code>, <code>{'{coupon_description}'}</code>, <code>{'{coupon_expiry}'}</code>
              </div>

              <div>
                <Label>Asunto del Email</Label>
                <Input
                  value={settings.template?.subject || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    template: { ...settings.template, subject: e.target.value }
                  })}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Cuerpo del Email</Label>
                <Textarea
                  value={settings.template?.email_body || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    template: { ...settings.template, email_body: e.target.value }
                  })}
                  className="mt-1 min-h-[150px] font-mono text-sm"
                />
              </div>

              <div>
                <Label>Mensaje SMS (máx. 160 caracteres)</Label>
                <Textarea
                  value={settings.template?.sms_body || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    template: { ...settings.template, sms_body: e.target.value }
                  })}
                  className="mt-1"
                  maxLength={160}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {(settings.template?.sms_body || '').length}/160
                </p>
              </div>

              <div>
                <Label>Mensaje WhatsApp</Label>
                <Textarea
                  value={settings.template?.whatsapp_body || ''}
                  onChange={(e) => setSettings({
                    ...settings,
                    template: { ...settings.template, whatsapp_body: e.target.value }
                  })}
                  className="mt-1 min-h-[120px]"
                />
              </div>

              <Button onClick={saveSettings} disabled={saving} className="w-full">
                {saving ? 'Guardando...' : 'Guardar Plantillas'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Historial de Envíos</CardTitle>
            </CardHeader>
            <CardContent>
              {history.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  No hay mensajes enviados aún
                </p>
              ) : (
                <div className="space-y-2">
                  {history.map((msg, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-muted rounded-xl">
                      <div className="flex items-center gap-3">
                        <Cake className="h-4 w-4 text-primary" />
                        <div>
                          <p className="text-sm font-medium">{msg.results?.customer_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(msg.sent_at).toLocaleDateString('es-MX')}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {msg.results?.email_sent && <Badge variant="outline">Email</Badge>}
                        {msg.results?.sms_sent && <Badge variant="outline">SMS</Badge>}
                        {msg.results?.whatsapp_sent && <Badge variant="outline">WhatsApp</Badge>}
                        {msg.results?.coupon_code && (
                          <Badge className="bg-green-500">{msg.results.coupon_code}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
